import { players, teamImageByName } from './data';
import { SeededRng } from './simulation/rng';
import type { ChampionsDraw, CompetitionResult, Player, PlayerStatKey, Position } from './types';

export const COMPETITION_SEED = 20261003;

/**
 * The displayed rating is a positional scouting summary, not a replacement
 * for the action-specific match formulas. Every row adds up to 100 so a
 * player with identical values in all attributes keeps that value regardless
 * of position, while irrelevant attributes no longer distort their profile.
 */
export const PLAYER_RATING_WEIGHTS: Record<Position, Record<PlayerStatKey, number>> = {
  GK: { kick: 2, dribble: 2, pass: 13, defense: 29, speed: 7, power: 14, control: 25, stamina: 8 },
  DF: { kick: 4, dribble: 6, pass: 12, defense: 28, speed: 15, power: 17, control: 8, stamina: 10 },
  MF: { kick: 11, dribble: 16, pass: 19, defense: 15, speed: 13, power: 8, control: 12, stamina: 6 },
  FW: { kick: 30, dribble: 20, pass: 7, defense: 2, speed: 17, power: 9, control: 12, stamina: 3 },
};

export interface RivalTeam {
  id: string;
  name: string;
  games: string[];
  players: Player[];
  rating: number;
  image_url?: string;
  /** Some special competitions provide an explicit tactical plan. */
  formation_id?: string;
  lineupPlayerIds?: string[];
  benchPlayerIds?: string[];
}

export interface LeagueGroup {
  id: string;
  name: string;
  teams: RivalTeam[];
}

export interface Fixture {
  homeId: string;
  awayId: string;
}

export interface Matchday {
  number: number;
  fixtures: Fixture[];
}

export interface LeagueStanding {
  team: RivalTeam;
  position: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

const EXCLUDED_TEAMS = new Set(['unaffiliated', 'unknown', 'sub character']);

export const rivalTeams = buildRivalTeams(players);
const GAME_SEASON_ORDER = [
  'Inazuma Eleven',
  'Inazuma Eleven 2: Firestorm / Blizzard',
  'Inazuma Eleven 3: Lightning Bolt / Bomb Blast / Team Ogre Attacks!',
  'Inazuma Eleven GO: Light / Shadow',
  'Inazuma Eleven GO Chrono Stones: Wildfire / Thunderflash',
  'Inazuma Eleven GO Galaxy: Big Bang / Supernova',
  'Inazuma Eleven Ares',
  'Inazuma Eleven Orion',
  'Inazuma Eleven: Victory Road',
];
export const gameSeasons = [...new Set(rivalTeams.flatMap((team) => team.games))].sort((a, b) => {
  const first = GAME_SEASON_ORDER.indexOf(a);
  const second = GAME_SEASON_ORDER.indexOf(b);
  return (first < 0 ? Number.MAX_SAFE_INTEGER : first) - (second < 0 ? Number.MAX_SAFE_INTEGER : second) || a.localeCompare(b);
});

export function buildRivalTeams(catalog: Player[]): RivalTeam[] {
  const grouped = new Map<string, { name: string; game: string; players: Player[] }>();
  for (const player of catalog) {
    const name = player.metadata?.team?.trim();
    if (!name || EXCLUDED_TEAMS.has(name.toLowerCase())) continue;
    const game = player.metadata?.game?.trim() || 'Temporada desconocida';
    const key = `${name.toLocaleLowerCase()}::${game.toLocaleLowerCase()}`;
    const current = grouped.get(key) ?? { name, game, players: [] };
    current.players.push(player);
    grouped.set(key, current);
  }
  return [...grouped.values()].map((team) => {
    const canonical = new Map<string, Player>();
    for (const player of team.players) {
      const key = player.name.toLocaleLowerCase();
      const existing = canonical.get(key);
      if (!existing || playerRating(player) > playerRating(existing)) canonical.set(key, player);
    }
    const teamPlayers = [...canonical.values()].sort((a, b) => playerRating(b) - playerRating(a));
    const strongest = teamPlayers.slice(0, 11);
    return {
      id: `rival_${slug(team.name)}_${slug(team.game)}`,
      name: team.name,
      games: [team.game],
      players: teamPlayers,
      rating: strongest.length ? Math.round(strongest.reduce((sum, player) => sum + playerRating(player), 0) / strongest.length) : 0,
      image_url: teamImageByName.get(team.name.toLocaleLowerCase()) || undefined,
    };
  }).sort((a, b) => a.name.localeCompare(b.name) || a.games[0].localeCompare(b.games[0]));
}

/** A side enters competitions only if it can field a real eleven: precisely
 * one goalkeeper plus ten outfield players. This prevents a second GK being
 * used as a silent fallback when a roster lacks a positional specialist. */
export function canFieldRivalTeam(team: RivalTeam): boolean {
  return team.players.some((player) => player.position === 'GK')
    && team.players.filter((player) => player.position !== 'GK').length >= 10;
}

/** Chooses a 4-4-2 compatible rival lineup without ever assigning a second
 * goalkeeper to an outfield role. The roster is already rating-sorted, so
 * each positional pick retains the strongest remaining eligible player. */
export function selectRivalLineup(team: RivalTeam): Player[] | null {
  if (team.lineupPlayerIds?.length === 11) {
    const roster = new Map(team.players.map((player) => [player.id, player]));
    const planned = team.lineupPlayerIds.flatMap((id) => roster.get(id) ?? []);
    if (planned.length === 11 && new Set(planned.map((player) => player.id)).size === 11
      && planned.filter((player) => player.position === 'GK').length === 1) return planned;
  }
  if (!canFieldRivalTeam(team)) return null;
  const available = [...team.players];
  const take = (positions: Player['position'][]) => {
    const index = available.findIndex((player) => positions.includes(player.position));
    return index >= 0 ? available.splice(index, 1)[0] : undefined;
  };
  const goalkeeper = take(['GK']);
  if (!goalkeeper) return null;
  const outfieldFallback: Player['position'][] = ['DF', 'MF', 'FW'];
  const slots: Player['position'][] = ['DF', 'DF', 'DF', 'DF', 'MF', 'MF', 'MF', 'MF', 'FW', 'FW'];
  const outfield = slots.map((position) => take([position]) ?? take(outfieldFallback));
  return outfield.some((player) => !player) ? null : [goalkeeper, ...outfield] as Player[];
}

export function createLeagueGroups(seed: number, clubName = 'Aurora Eleven', groupCount = 16): LeagueGroup[] {
  const rng = new SeededRng(seed);
  const shuffled = [
    ...rivalTeams.filter(canFieldRivalTeam),
    { id: 'team_aurora', name: clubName, games: ['Club del jugador'], players: [], rating: 74 },
  ];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const target = rng.int(0, index);
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }
  const groups = Array.from({ length: groupCount }, (_, index) => ({
    id: `league_group_${index + 1}`,
    name: `Grupo ${String.fromCharCode(65 + index)}`,
    teams: [] as RivalTeam[],
  }));
  shuffled.forEach((team, index) => groups[index % groupCount].teams.push(team));
  return groups;
}

export function createDoubleRoundRobinSchedule(teamIds: string[]): Matchday[] {
  const rotation: (string | null)[] = [...teamIds];
  if (rotation.length % 2 !== 0) rotation.push(null);
  const firstLeg: Matchday[] = [];
  for (let round = 0; round < rotation.length - 1; round++) {
    const fixtures: Fixture[] = [];
    for (let pair = 0; pair < rotation.length / 2; pair++) {
      const first = rotation[pair];
      const second = rotation[rotation.length - 1 - pair];
      if (first && second) {
        const reverse = (round + pair) % 2 === 1;
        fixtures.push({ homeId: reverse ? second : first, awayId: reverse ? first : second });
      }
    }
    firstLeg.push({ number: round + 1, fixtures });
    rotation.splice(1, 0, rotation.pop()!);
  }
  const secondLeg = firstLeg.map((matchday, index) => ({
    number: firstLeg.length + index + 1,
    fixtures: matchday.fixtures.map((fixture) => ({ homeId: fixture.awayId, awayId: fixture.homeId })),
  }));
  return [...firstLeg, ...secondLeg];
}

export function findClubGroup(groups: LeagueGroup[]): LeagueGroup {
  return groups.find((group) => group.teams.some((team) => team.id === 'team_aurora')) ?? groups[0];
}

export function findClubFixture(group: LeagueGroup, matchdayIndex: number): { matchday: Matchday; fixture: Fixture } | null {
  const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
  if (!schedule.length) return null;
  const matchday = schedule[Math.max(0, matchdayIndex) % schedule.length];
  const fixture = matchday.fixtures.find((item) => item.homeId === 'team_aurora' || item.awayId === 'team_aurora');
  return fixture ? { matchday, fixture } : null;
}

export function createStandings(group: LeagueGroup, results: CompetitionResult[], pointAdjustments: Record<string, number> = {}): LeagueStanding[] {
  const rows = new Map(group.teams.map((team) => [team.id, {
    team,
    position: 0,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: Math.round(Number(pointAdjustments[team.id]) || 0),
  }]));
  results.filter((result) => result.competitionId === group.id).forEach((result) => {
    const home = rows.get(result.homeId);
    const away = rows.get(result.awayId);
    if (!home || !away) return;
    home.played += 1;
    away.played += 1;
    home.goalsFor += result.homeScore;
    home.goalsAgainst += result.awayScore;
    away.goalsFor += result.awayScore;
    away.goalsAgainst += result.homeScore;
    if (result.homeScore > result.awayScore) {
      home.won += 1; home.points += 3; away.lost += 1;
    } else if (result.homeScore < result.awayScore) {
      away.won += 1; away.points += 3; home.lost += 1;
    } else {
      home.drawn += 1; away.drawn += 1; home.points += 1; away.points += 1;
    }
  });
  return [...rows.values()]
    .map((row) => ({ ...row, goalDifference: row.goalsFor - row.goalsAgainst }))
    .sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference || b.goalsFor - a.goalsFor || b.team.rating - a.team.rating || a.team.name.localeCompare(b.team.name))
    .map((row, index) => ({ ...row, position: index + 1 }));
}

export function simulateScore(home: RivalTeam, away: RivalTeam, seed: number): [number, number] {
  const rng = new SeededRng(seed);
  const difference = (home.rating + 2 - away.rating) / 18;
  const homeScore = Math.max(0, Math.min(6, Math.floor(rng.between(0, 3.35) + Math.max(-0.55, difference))));
  const awayScore = Math.max(0, Math.min(6, Math.floor(rng.between(0, 3.05) + Math.max(-0.55, -difference))));
  return [homeScore, awayScore];
}

export function completeLeagueMatchday(
  group: LeagueGroup,
  matchdayIndex: number,
  clubScore: [number, number],
  seed: number,
): CompetitionResult[] {
  const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
  const matchday = schedule[Math.max(0, matchdayIndex) % schedule.length];
  const teamById = new Map(group.teams.map((team) => [team.id, team]));
  return matchday.fixtures.map((fixture, index) => {
    const involvesClub = fixture.homeId === 'team_aurora' || fixture.awayId === 'team_aurora';
    const [homeScore, awayScore] = involvesClub
      ? clubScore
      : simulateScore(teamById.get(fixture.homeId)!, teamById.get(fixture.awayId)!, seed + index * 104729);
    return { competitionId: group.id, matchday: matchday.number, ...fixture, homeScore, awayScore };
  });
}

/** Fills the non-player matches at the end of a season without replacing the
 * real matches already registered for the manager's league. */
export function completeLeagueSeason(groups: LeagueGroup[], existingResults: CompetitionResult[], seed: number): CompetitionResult[] {
  const stored = new Map(existingResults.map((result) => [`${result.competitionId}:${result.matchday}:${result.homeId}:${result.awayId}`, result]));
  const allResults: CompetitionResult[] = [];
  groups.forEach((group, groupIndex) => {
    const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
    const teamById = new Map(group.teams.map((team) => [team.id, team]));
    schedule.forEach((matchday) => matchday.fixtures.forEach((fixture, fixtureIndex) => {
      const key = `${group.id}:${matchday.number}:${fixture.homeId}:${fixture.awayId}`;
      const saved = stored.get(key);
      if (saved) {
        allResults.push(saved);
        return;
      }
      const [homeScore, awayScore] = simulateScore(
        teamById.get(fixture.homeId)!,
        teamById.get(fixture.awayId)!,
        seed + groupIndex * 1_000_003 + matchday.number * 1_009 + fixtureIndex * 37,
      );
      allResults.push({ competitionId: group.id, matchday: matchday.number, ...fixture, homeScore, awayScore });
    }));
  });
  return allResults;
}

/** Draws first-versus-second pairings while avoiding the source league whenever
 * possible. It stays persisted so visiting the page never rerolls the draw. */
export function createChampionsDraw(groups: LeagueGroup[], results: CompetitionResult[], season: number, seed: number, pointAdjustments: Record<string, number> = {}): ChampionsDraw {
  const rng = new SeededRng(seed);
  const winners = groups.map((group) => ({ groupId: group.id, teamId: createStandings(group, results, pointAdjustments)[0]?.team.id })).filter((entry): entry is { groupId: string; teamId: string } => Boolean(entry.teamId));
  const runnersUp = groups.map((group) => ({ groupId: group.id, teamId: createStandings(group, results, pointAdjustments)[1]?.team.id })).filter((entry): entry is { groupId: string; teamId: string } => Boolean(entry.teamId));
  shuffle(winners, rng);
  const remainingRunnersUp = [...runnersUp];
  const pairings = winners.map((winner, index) => {
    const eligible = remainingRunnersUp.map((entry, runnerIndex) => ({ entry, runnerIndex })).filter(({ entry }) => entry.groupId !== winner.groupId);
    const selected = eligible.length ? eligible[rng.int(0, eligible.length - 1)] : { entry: remainingRunnersUp[0], runnerIndex: 0 };
    remainingRunnersUp.splice(selected.runnerIndex, 1);
    return { id: `champions_s${season}_${index + 1}`, firstTeamId: winner.teamId, firstGroupId: winner.groupId, secondTeamId: selected.entry.teamId, secondGroupId: selected.entry.groupId };
  });
  return { season, sourceSeason: season - 1, seed, pairings };
}

/** A different, deterministic seed is generated at each rollover. */
export function nextLeagueSeed(previousSeed: number, season: number, entropy: number): number {
  const rng = new SeededRng((previousSeed ^ entropy ^ (season * 1_048_583)) >>> 0);
  return rng.int(10_000_000, 2_000_000_000);
}

export function playerRating(player: Player): number {
  const weights = PLAYER_RATING_WEIGHTS[player.position];
  if (!weights) return 0;
  const weightedTotal = (Object.entries(weights) as Array<[PlayerStatKey, number]>).reduce((sum, [stat, weight]) => (
    sum + Math.max(0, Number(player.stats[stat]) || 0) * weight
  ), 0);
  return Math.round(weightedTotal / 100);
}

export function teamSeasonCode(team: RivalTeam): string {
  const game = team.games[0] ?? '';
  if (game.includes('Victory Road')) return 'VR';
  if (game.includes('Orion')) return 'Orion';
  if (game.includes('Ares')) return 'Ares';
  if (game.includes('Galaxy')) return 'Galaxy';
  if (game.includes('Chrono Stones')) return 'CS';
  if (game.includes('GO:')) return 'GO';
  if (game.includes('Eleven 3')) return 'IE3';
  if (game.includes('Eleven 2')) return 'IE2';
  if (game === 'Club del jugador') return 'Club';
  return 'IE1';
}

function slug(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function shuffle<T>(items: T[], rng: SeededRng) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const target = rng.int(0, index);
    [items[index], items[target]] = [items[target], items[index]];
  }
}
